/**
 * GameDataPanel - Display player analysis, tactical breakdown, and recommendations
 * Bento grid layout with expandable cards
 */

import React, { useState } from 'react';
import {
  User,
  Users,
  Target,
  Shield,
  TrendingUp,
  TrendingDown,
  AlertTriangle,
  ChevronDown,
  ChevronUp,
  Activity,
  Zap,
  Star,
  Clock,
} from 'lucide-react';

// Collapsible section component - Modern OLED design
const Section = ({ title, icon: Icon, children, defaultOpen = true, badge }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <div className="bg-black rounded-2xl border border-white/[0.15] overflow-hidden">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between px-5 py-4 hover:bg-white/[0.03] transition-colors"
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-yellow-500/20 to-orange-500/10 flex items-center justify-center">
            <Icon className="w-5 h-5 text-yellow-400" />
          </div>
          <span className="text-white font-semibold text-base">{title}</span>
          {badge && (
            <span className="px-2.5 py-1 bg-yellow-400/10 text-yellow-400 text-xs font-semibold rounded-full border border-yellow-400/20">
              {badge}
            </span>
          )}
        </div>
        <div className={`w-8 h-8 rounded-full flex items-center justify-center transition-colors ${isOpen ? 'bg-white/10' : 'bg-white/5'}`}>
          {isOpen ? (
            <ChevronUp className="w-4 h-4 text-white/80" />
          ) : (
            <ChevronDown className="w-4 h-4 text-white/60" />
          )}
        </div>
      </button>
      {isOpen && <div className="px-5 pb-5 pt-1">{children}</div>}
    </div>
  );
};

// Player card component
const PlayerCard = ({ player, isExpanded, onToggle }) => {
  const teamColors = player.team === 'Real Madrid'
    ? { bg: 'bg-white/[0.06]', border: 'border-white/[0.18]', badge: 'bg-white text-black' }
    : { bg: 'bg-blue-900/25', border: 'border-blue-500/30', badge: 'bg-blue-600 text-white' };

  return (
    <div className={`rounded-lg border backdrop-blur-sm ${teamColors.border} ${teamColors.bg} overflow-hidden`}>
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between p-3 hover:bg-white/[0.05] transition-colors"
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-white/[0.08] flex items-center justify-center">
            <User className="w-5 h-5 text-white/60" />
          </div>
          <div className="text-left">
            <h4 className="text-white font-semibold text-sm">{player.player}</h4>
            <div className="flex items-center gap-2 mt-0.5">
              <span className={`px-1.5 py-0.5 text-xs font-medium rounded ${teamColors.badge}`}>
                {player.team}
              </span>
              <span className="text-white/50 text-xs">{player.role}</span>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {player.fatigueStatus?.includes('Degraded') && (
            <AlertTriangle className="w-4 h-4 text-amber-400" />
          )}
          {isExpanded ? (
            <ChevronUp className="w-4 h-4 text-white/60" />
          ) : (
            <ChevronDown className="w-4 h-4 text-white/60" />
          )}
        </div>
      </button>

      {isExpanded && (
        <div className="px-3 pb-3 space-y-3 border-t border-white/10">
          {/* Biomechanics */}
          <div className="pt-3">
            <h5 className="text-white/60 text-xs uppercase tracking-wider mb-2 flex items-center gap-1">
              <Activity className="w-3 h-3" /> Biomechanics
            </h5>
            <div className="space-y-2">
              {Object.entries(player.biomechanics || {}).map(([key, value]) => (
                <div key={key} className="bg-black/30 rounded-lg p-2">
                  <span className="text-yellow-400 text-xs font-medium capitalize block mb-1">
                    {key.replace(/([A-Z])/g, ' $1').trim()}
                  </span>
                  <p className="text-white/80 text-xs leading-relaxed">{value}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Tactical Impact */}
          {player.tacticalImpact && (
            <div>
              <h5 className="text-white/60 text-xs uppercase tracking-wider mb-2 flex items-center gap-1">
                <Target className="w-3 h-3" /> Tactical Impact
              </h5>
              <p className="text-white/80 text-xs leading-relaxed bg-black/30 rounded-lg p-2">
                {player.tacticalImpact}
              </p>
            </div>
          )}

          {/* Fatigue Status */}
          {player.fatigueStatus && (
            <div className={`flex items-center gap-2 p-2 rounded-lg ${
              player.fatigueStatus.includes('Degraded') || player.fatigueStatus.includes('exposed')
                ? 'bg-amber-500/10 border border-amber-500/30'
                : 'bg-green-500/10 border border-green-500/30'
            }`}>
              <Clock className={`w-4 h-4 ${
                player.fatigueStatus.includes('Degraded') || player.fatigueStatus.includes('exposed')
                  ? 'text-amber-400' : 'text-green-400'
              }`} />
              <span className="text-white/80 text-xs">{player.fatigueStatus}</span>
            </div>
          )}

          {/* CSV Reference */}
          {player.csvReference && (
            <div className="text-white/40 text-xs font-mono">
              📊 {player.csvReference}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Recommendation card
const RecommendationCard = ({ rec }) => {
  const priorityColors = {
    high: 'border-red-500 bg-red-500/10',
    medium: 'border-yellow-500 bg-yellow-500/10',
    low: 'border-green-500 bg-green-500/10',
  };

  const teamColors = rec.team === 'Real Madrid'
    ? 'bg-white text-black'
    : rec.team === 'FC Barcelona'
    ? 'bg-blue-600 text-white'
    : 'bg-zinc-600 text-white';

  return (
    <div className={`rounded-lg border p-3 ${priorityColors[rec.priority] || priorityColors.medium}`}>
      <div className="flex items-center gap-2 mb-2">
        <span className={`px-2 py-0.5 text-xs font-bold rounded ${teamColors}`}>
          {rec.team}
        </span>
        <span className="text-white/60 text-xs">•</span>
        <span className="text-white font-medium text-sm">{rec.focus}</span>
      </div>
      <p className="text-white/80 text-sm leading-relaxed">{rec.action}</p>
    </div>
  );
};

// Tactical breakdown card with team colors
const TacticalCard = ({ title, data, icon: Icon, teamColor = 'yellow' }) => {
  const colorSchemes = {
    blue: {
      bg: 'bg-gradient-to-br from-blue-950/40 to-blue-900/20',
      border: 'border-blue-500/30',
      accent: 'text-blue-400',
      icon: 'bg-blue-500/20 text-blue-400',
      dot: 'bg-blue-500',
    },
    white: {
      bg: 'bg-gradient-to-br from-white/[0.08] to-white/[0.03]',
      border: 'border-white/25',
      accent: 'text-white/90',
      icon: 'bg-white/15 text-white',
      dot: 'bg-white',
    },
    yellow: {
      bg: 'bg-gradient-to-br from-yellow-950/30 to-yellow-900/10',
      border: 'border-yellow-500/30',
      accent: 'text-yellow-400',
      icon: 'bg-yellow-500/20 text-yellow-400',
      dot: 'bg-yellow-500',
    },
  };
  const colors = colorSchemes[teamColor] || colorSchemes.yellow;

  return (
    <div className={`rounded-xl p-4 border backdrop-blur-sm ${colors.bg} ${colors.border}`}>
      <div className="flex items-center gap-3 mb-4">
        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${colors.icon}`}>
          <Icon className="w-4 h-4" />
        </div>
        <div className="flex items-center gap-2">
          <div className={`w-2 h-2 rounded-full ${colors.dot}`} />
          <h4 className="text-white font-semibold text-sm">{title}</h4>
        </div>
      </div>
      <div className="space-y-3">
        {Object.entries(data).map(([key, value]) => {
          if (typeof value === 'object') return null;
          return (
            <div key={key} className="bg-black/30 rounded-lg p-2.5">
              <span className={`text-xs font-medium capitalize block mb-1 ${colors.accent}`}>
                {key.replace(/([A-Z])/g, ' $1').trim()}
              </span>
              <p className="text-white/80 text-xs leading-relaxed">{value}</p>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const GameDataPanel = ({
  playerAnalysis = [],
  tacticalBreakdown = {},
  recommendations = [],
  benchDynamics = {},
  translations = {},
}) => {
  const [expandedPlayers, setExpandedPlayers] = useState(new Set());

  const togglePlayer = (playerName) => {
    setExpandedPlayers((prev) => {
      const next = new Set(prev);
      if (next.has(playerName)) {
        next.delete(playerName);
      } else {
        next.add(playerName);
      }
      return next;
    });
  };

  // Split players by team
  const realMadridPlayers = playerAnalysis.filter((p) => p.team === 'Real Madrid');
  const barcelonaPlayers = playerAnalysis.filter((p) => p.team === 'FC Barcelona');

  return (
    <div className="space-y-4 overflow-y-auto">
      {/* Key Recommendations - Always visible at top */}
      <Section
        title={translations.recommendations || "Key Recommendations"}
        icon={Star}
        badge={`${recommendations.length}`}
        defaultOpen={true}
      >
        <div className="space-y-3">
          {recommendations.map((rec, idx) => (
            <RecommendationCard key={idx} rec={rec} />
          ))}
        </div>
      </Section>

      {/* Tactical Breakdown */}
      <Section
        title={translations.tacticalBreakdown || "Tactical Breakdown"}
        icon={Target}
        defaultOpen={true}
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {tacticalBreakdown.barcelonaOffense && (
            <TacticalCard
              title="Barcelona Offense"
              data={tacticalBreakdown.barcelonaOffense}
              icon={TrendingUp}
              teamColor="blue"
            />
          )}
          {tacticalBreakdown.barcelonaDefense && (
            <TacticalCard
              title="Barcelona Defense"
              data={tacticalBreakdown.barcelonaDefense}
              icon={Shield}
              teamColor="blue"
            />
          )}
          {tacticalBreakdown.realMadridOffense && (
            <TacticalCard
              title="Real Madrid Offense"
              data={tacticalBreakdown.realMadridOffense}
              icon={TrendingUp}
              teamColor="white"
            />
          )}
          {tacticalBreakdown.realMadridDefense && (
            <TacticalCard
              title="Real Madrid Defense"
              data={tacticalBreakdown.realMadridDefense}
              icon={Shield}
              teamColor="white"
            />
          )}
        </div>
      </Section>

      {/* Player Analysis */}
      <Section
        title={translations.playerAnalysis || "Player Analysis"}
        icon={Users}
        badge={`${playerAnalysis.length} players`}
        defaultOpen={false}
      >
        <div className="space-y-4">
          {/* Real Madrid */}
          <div>
            <h4 className="text-white/60 text-xs uppercase tracking-wider mb-2 flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-white" />
              Real Madrid
            </h4>
            <div className="space-y-2">
              {realMadridPlayers.map((player) => (
                <PlayerCard
                  key={player.player}
                  player={player}
                  isExpanded={expandedPlayers.has(player.player)}
                  onToggle={() => togglePlayer(player.player)}
                />
              ))}
            </div>
          </div>

          {/* Barcelona */}
          <div>
            <h4 className="text-white/60 text-xs uppercase tracking-wider mb-2 flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-blue-600" />
              FC Barcelona
            </h4>
            <div className="space-y-2">
              {barcelonaPlayers.map((player) => (
                <PlayerCard
                  key={player.player}
                  player={player}
                  isExpanded={expandedPlayers.has(player.player)}
                  onToggle={() => togglePlayer(player.player)}
                />
              ))}
            </div>
          </div>
        </div>
      </Section>

      {/* Bench Dynamics */}
      <Section
        title={translations.benchDynamics || "Bench Dynamics"}
        icon={Activity}
        defaultOpen={false}
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {/* Barcelona Bench */}
          {benchDynamics.barcelona && (
            <div className="bg-blue-900/15 rounded-lg p-3 border border-blue-500/20 backdrop-blur-sm">
              <h4 className="text-white font-semibold text-sm mb-2 flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-blue-500" />
                FC Barcelona
                <span className="ml-auto text-green-400 text-xs flex items-center gap-1">
                  <TrendingUp className="w-3 h-3" />
                  {benchDynamics.barcelona.energyCurve}
                </span>
              </h4>
              <div className="space-y-2 text-xs text-white/80">
                <p><strong>Engagement:</strong> {benchDynamics.barcelona.engagement}</p>
                <p><strong>Coach:</strong> {benchDynamics.barcelona.coachSignals}</p>
              </div>
            </div>
          )}

          {/* Real Madrid Bench */}
          {benchDynamics.realMadrid && (
            <div className="bg-white/[0.04] rounded-lg p-3 border border-white/[0.18] backdrop-blur-sm">
              <h4 className="text-white font-semibold text-sm mb-2 flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-white" />
                Real Madrid
                <span className="ml-auto text-red-400 text-xs flex items-center gap-1">
                  <TrendingDown className="w-3 h-3" />
                  {benchDynamics.realMadrid.energyCurve}
                </span>
              </h4>
              <div className="space-y-2 text-xs text-white/80">
                <p><strong>Engagement:</strong> {benchDynamics.realMadrid.engagement}</p>
                <p><strong>Issues:</strong> {benchDynamics.realMadrid.frustrationSignals}</p>
              </div>
            </div>
          )}
        </div>
      </Section>
    </div>
  );
};

export default GameDataPanel;
